/**
 * Toolbar — Floating toolbar component
 * Per PLAN.md ui/Toolbar.js and PRD §5.2
 */

import { eventBus, Events } from '../core/EventBus.js';
import { TOOL, THEME } from '../utils/constants.js';

export class Toolbar {
  /**
   * @param {HTMLElement} container 
   */
  constructor(container) {
    this.container = container;
    this.currentTool = TOOL.SELECT;
    this.element = null;

    this.init();
  }

  init() {
    this.element = document.createElement('div');
    this.element.className = 'toolbar';
    this.element.innerHTML = `
      <button class="toolbar-btn active" data-tool="select" title="Select (V)">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M5 3l14 9-6 2-4 6-4-17z"/>
        </svg>
      </button>
      <button class="toolbar-btn" data-tool="connect" title="Connect (C)">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </button>
      <div class="toolbar-divider"></div>
      <button class="toolbar-btn" data-action="add" title="Add Node (N)">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 5v14M5 12h14"/>
        </svg>
      </button>
      <div class="toolbar-divider"></div>
      <button class="toolbar-btn" data-action="grid" title="Toggle Grid (⌘G)">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <path d="M3 9h18M3 15h18M9 3v18M15 3v18"/>
        </svg>
      </button>
      <button class="toolbar-btn" data-action="zoomIn" title="Zoom In (⌘+)">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/>
          <path d="M21 21l-4.35-4.35M11 8v6M8 11h6"/>
        </svg>
      </button>
      <button class="toolbar-btn" data-action="zoomOut" title="Zoom Out (⌘-)">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/>
          <path d="M21 21l-4.35-4.35M8 11h6"/>
        </svg>
      </button>
      <div class="toolbar-divider"></div>
      <button class="toolbar-btn" data-action="theme" title="Toggle Theme">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="5"/>
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
        </svg>
      </button>
      <div class="toolbar-divider"></div>
      <button class="toolbar-btn" data-action="erase" title="Erase Board">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
        </svg>
      </button>
    `;

    this.container.appendChild(this.element);
    this.bindEvents();
  }

  bindEvents() {
    // Tool buttons
    this.element.querySelectorAll('[data-tool]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.setTool(btn.dataset.tool);
      });
    });

    // Action buttons
    this.element.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.handleAction(btn.dataset.action);
      });
    });

    // Listen for external tool changes
    eventBus.on(Events.TOOL_CHANGE, ({ tool }) => {
      this.updateActiveButton(tool);
    });
  }

  /**
   * Set active tool
   * @param {string} tool 
   */
  setTool(tool) {
    this.currentTool = tool;
    this.updateActiveButton(tool);
    eventBus.emit(Events.TOOL_CHANGE, { tool });
  }

  /**
   * Update active button state
   * @param {string} tool 
   */
  updateActiveButton(tool) {
    this.element.querySelectorAll('[data-tool]').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tool === tool);
    });
  }

  /**
   * Handle action button click
   * @param {string} action 
   */
  handleAction(action) {
    switch (action) {
      case 'add':
        eventBus.emit('toolbar:add', {});
        break;
      case 'grid':
        eventBus.emit('toolbar:grid', {});
        break;
      case 'zoomIn':
        eventBus.emit('toolbar:zoomIn', {});
        break;
      case 'zoomOut':
        eventBus.emit('toolbar:zoomOut', {});
        break;
      case 'theme':
        eventBus.emit('toolbar:theme', {});
        break;
      case 'erase':
        eventBus.emit('toolbar:erase', {});
        break;
    }
  }

  /**
   * Get current tool
   * @returns {string}
   */
  getCurrentTool() {
    return this.currentTool;
  }

  /**
   * Destroy toolbar
   */
  destroy() {
    this.element.remove();
  }
}
